/**
 * Back In Stock Manager
 *
 * Manages functionality for the back in stock notification system.
 * Allows customers to subscribe for notifications when out-of-stock products become available.
 */

/**
 * Manages Back in Stock notification functionality
 */
class BackInStockDialog extends ModalDialog {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.companyId = window.klaviyoId;
    this.sectionId = this.dataset.section;
    this.notifyMeHashName = 'notify-me';

    this.attachEventListeners();
  }

  disconnectedCallback() {
    // Remove event listeners if necessary
    const form = this.querySelector('.back-in-stock-form');
    if (form) {
      form.removeEventListener('submit', this.handleFormSubmitBound);
    }
  }

  /**
   * Attaches event listeners to forms and trigger buttons
   */
  attachEventListeners() {
    // Attach form submit handlers
    this.handleFormSubmitBound = this.handleFormSubmit.bind(this);
    this.querySelector('.back-in-stock-form').addEventListener('submit', this.handleFormSubmitBound);
  }

  show(opener) {
    const infoContainer = document.querySelector(`product-info#MainProduct-${this.sectionId}`);
    const selectedVariantElement = infoContainer?.querySelector('[data-selected-variant]');

    if (!selectedVariantElement) {
      console.log('No selected variant data found.', infoContainer, selectedVariantElement);
      return;
    }

    // Parse variant data
    let selectedVariantData;
    try {
      selectedVariantData = JSON.parse(selectedVariantElement.textContent);
      if (!selectedVariantData?.id || !selectedVariantData?.name) {
        throw new Error('Incomplete variant data');
      }
    } catch (e) {
      console.log('Error parsing selected variant data:', e);
      return;
    }

    // Update modal content with variant info
    const modalOptionDetails = this.querySelector('.back-in-stock-content__details');
    const variantIdInput = this.querySelector('input[name="variant_id"]');

    if (modalOptionDetails) modalOptionDetails.textContent = selectedVariantData.name;
    if (variantIdInput) variantIdInput.value = selectedVariantData.id;

    // Reset form
    this.resetForm(this.querySelector('.back-in-stock-form'));

    // Show modal
    super.show(opener);
  }

  hide() {
    super.hide();
    // remove the notify-me hash from the URL
    history.pushState('', document.title, window.location.pathname + window.location.search);
  }

  /**
   * Handles the back in stock form submission
   * @param {Event} event - The form submit event
   */
  handleFormSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const emailInput = form.querySelector('input[name="email"]');
    const variantIdInput = form.querySelector('input[name="variant_id"]');
    const submitBtn = form.querySelector('button[type="submit"]');
    const buttonText = submitBtn.querySelector('.button__text');
    const messageEl = form.querySelector('.back-in-stock-form__message');

    // Reset UI state
    messageEl.style.display = 'none';
    messageEl.className = 'back-in-stock-form__message';
    form.querySelectorAll('.field__error').forEach(function hideErrors(el) {
      el.style.display = 'none';
    });
    emailInput.removeAttribute('aria-invalid');

    // Validate inputs
    if (!this.companyId) {
      this.showFormMessage(messageEl, 'Configuration error. Missing Klaviyo ID.', 'error');
      return;
    }

    if (!emailInput.value || !this.validateEmail(emailInput)) {
      this.showFormMessage(messageEl, 'Please enter a valid email address.', 'error');
      return;
    }

    // Show loading state
    submitBtn.disabled = true;
    submitBtn.classList.add('loading');
    buttonText.textContent = 'Subscribing...';

    // Submit the subscription request
    this.submitBackInStockRequest(form, emailInput, variantIdInput, submitBtn, buttonText, messageEl);
  }

  /**
   * Submits the back in stock request to Klaviyo
   * @param {HTMLFormElement} form - The form element
   * @param {HTMLInputElement} emailInput - The email input field
   * @param {HTMLInputElement} variantIdInput - The variant ID input field
   * @param {HTMLButtonElement} submitBtn - The submit button
   * @param {HTMLElement} buttonText - The button text element
   * @param {HTMLElement} messageEl - The message display element
   */
  submitBackInStockRequest(form, emailInput, variantIdInput, submitBtn, buttonText, messageEl) {
    // Prepare API request
    const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.companyId}`;
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: emailInput.value
              }
            }
          },
          channels: ["EMAIL"]
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantIdInput.value}`
            }
          }
        }
      }
    };

    // Send request to Klaviyo
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-06-15' },
      body: JSON.stringify(payload)
    })
      .then(function handleResponse(response) {
        if (response.status === 202 || response.ok) {
          this.showFormMessage(messageEl, 'Success! We will notify you when this item is back in stock.', 'success');
          form.reset();
          buttonText.textContent = 'Subscribed!';
          // Hide the email field on success
          const emailField = emailInput.closest('.field');
          if (emailField) emailField.style.display = 'none';

          // Remove the notify-me hash from the URL after successful subscription
          if (window.location.hash === `#${this.notifyMeHashName}`) {
            history.replaceState(null, '', window.location.pathname + window.location.search);
          }

          return;
        }

        // Handle error responses
        return response.json().then(function handleErrorData(errorData) {
          const errorMessage = errorData?.errors?.[0]?.detail || 'Unable to subscribe. Please try again.';
          this.showFormMessage(messageEl, errorMessage, 'error');
          submitBtn.disabled = false;
          throw new Error(errorMessage);
        }.bind(this));
      }.bind(this))
      .catch(function handleError(error) {
        console.error('Back in stock subscription error:', error);
        // Only show network error if no message is already displayed
        if (messageEl.style.display === 'none') {
          this.showFormMessage(messageEl, 'Connection error. Please try again later.', 'error');
        }

        // Reset button state if not successful
        if (!messageEl.classList.contains('success')) {
          submitBtn.disabled = false;
          const emailField = emailInput.closest('.field');
          if (emailField) emailField.style.display = '';
        }
      }.bind(this))
      .finally(function cleanupAfterRequest() {
        submitBtn.classList.remove('loading');
      });
  }

  /**
   * Validates an email input field
   * @param {HTMLInputElement} emailInput - The email input to validate
   * @returns {boolean} - True if valid, false otherwise
   */
  validateEmail(emailInput) {
    const errorElement = emailInput.parentElement.nextElementSibling;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(emailInput.value);

    if (!isValid) {
      emailInput.setAttribute('aria-invalid', 'true');
      if (errorElement) errorElement.style.display = 'block';
    } else {
      emailInput.removeAttribute('aria-invalid');
      if (errorElement) errorElement.style.display = 'none';
    }

    return isValid;
  }

  /**
   * Resets a form to its initial state
   * @param {HTMLFormElement} form - The form to reset
   */
  resetForm(form) {
    if (!form) return;

    form.reset();

    const messageEl = form.querySelector('.back-in-stock-form__message');
    const submitBtn = form.querySelector('button[type="submit"]');
    const buttonText = submitBtn?.querySelector('.button__text');
    const emailField = form.querySelector('input[name="email"]')?.closest('.field');

    if (messageEl) messageEl.style.display = 'none';
    if (submitBtn) submitBtn.disabled = false;
    if (buttonText) buttonText.textContent = 'Notify Me';
    // Ensure email field is visible on reset
    if (emailField) emailField.style.display = '';

    form.querySelectorAll('.field__error').forEach(function hideErrors(el) {
      el.style.display = 'none';
    });
    form.querySelectorAll('.field__input[aria-invalid]').forEach(function removeInvalid(el) {
      el.removeAttribute('aria-invalid');
    });
  }

  /**
   * Displays a message in the form
   * @param {HTMLElement} messageEl - The message container element
   * @param {string} message - The message to display
   * @param {string} type - The message type (success or error)
   */
  showFormMessage(messageEl, message, type) {
    messageEl.textContent = message;
    messageEl.className = `back-in-stock-form__message ${type}`;
    messageEl.style.display = 'block';

    // Ensure email field is visible when showing any message initially
    const form = messageEl.closest('form');
    const emailField = form?.querySelector('input[name="email"]')?.closest('.field');
    if (emailField && type !== 'success') {
      emailField.style.display = '';
    }
  }

  /**
   * Displays an error message in a user-friendly toast or dialog
   * @param {string} message - The error message to display
   */
  showError(message) {
    // This could be replaced with a toast notification system
    // For now, we'll use a simple alert
    alert(message);
  }
}
customElements.define('back-in-stock-dialog', BackInStockDialog);

// Export the BackInStockDialog for use in other files
window.BackInStockDialog = BackInStockDialog;